import { getLastOptionIdOrNull } from '../../util/ui-helper';
import { CustomFieldType } from '../../../model/model';
import { FieldEditorOptionActionSetDefault, FieldEditorOptionActionUnSetDefault } from '../field-editor/FieldEditor';
import { DTOGetDefaultOption, DTOCustomFieldOption, DTOGetDefaultOptionItem, DTOSetDefaultOption } from 'generated-common/model/model';

export class DTOGetDefaultOptionImpl implements DTOGetDefaultOption {
  constructor(public defaultOption?: DTOGetDefaultOptionItem, public defaultChildOption?: DTOGetDefaultOptionItem, public defaultOptions?: DTOGetDefaultOptionItem[]) {}
}
export class DTOGetDefaultOptionItemImpl implements DTOGetDefaultOptionItem {
  constructor(public optionId: string, public optionValue: string) {}
}

//
// INTERNAL HELPER FUNCTIONS
//
export const __hasDefaultOptionWithDefinedId = (defaultOption: DTOGetDefaultOption) => {
  return defaultOption !== undefined && defaultOption !== null && defaultOption.defaultOption !== undefined && defaultOption.defaultOption.optionId !== undefined;
};
export const __hasDefaultChildOptionWithDefinedId = (defaultOption: DTOGetDefaultOption) => {
  return defaultOption !== undefined && defaultOption !== null && defaultOption.defaultChildOption !== undefined && defaultOption.defaultChildOption.optionId !== undefined;
};
export const __hasDefaultMultiOptionsWithDefinedIds = (defaultOption: DTOGetDefaultOption) => {
  return defaultOption !== undefined && defaultOption !== null && defaultOption.defaultOptions !== undefined && defaultOption.defaultOptions !== null && defaultOption.defaultOptions.length !== 0;
};
export const __hasDifferentDefaultMultiOptionsWithDefinedIds = (prevDefaultOption: DTOGetDefaultOption, nextDefaultOption: DTOGetDefaultOption) => {
  if (__hasDefaultMultiOptionsWithDefinedIds(prevDefaultOption) && __hasDefaultMultiOptionsWithDefinedIds(nextDefaultOption)) {
    const unsortedPrevIds = prevDefaultOption!.defaultOptions!.map((o) => o.optionId);
    const unsortedNextIds = nextDefaultOption!.defaultOptions!.map((o) => o.optionId);
    unsortedPrevIds.sort();
    unsortedNextIds.sort();
    const prevIdsString = unsortedPrevIds.join(',');
    const nextIdsString = unsortedNextIds.join(',');

    return prevIdsString !== nextIdsString;
  } else {
    return false;
  }
};
export const __isEmptyDefaultOptionObject = (defaultOption: DTOGetDefaultOption) => {
  return defaultOption !== undefined && defaultOption !== null && Object.keys(defaultOption).length === 0;
};
//
// CLASS
//

export class DefaultOptionHelper {
  public static hasSingleDefaultValue(defaultOption: DTOGetDefaultOption, fieldType: CustomFieldType): boolean {
    return (
      defaultOption && (fieldType === CustomFieldType.SINGLESEELCT || fieldType === CustomFieldType.RADIOBUTTONS) && defaultOption.defaultOption !== undefined && defaultOption.defaultOption !== null
    );
  }
  public static hasCascadingDefaultValues(defaultOption: DTOGetDefaultOption, fieldType: CustomFieldType): boolean {
    return (
      defaultOption && fieldType === CustomFieldType.CASCADINGSELECT && defaultOption.defaultOption !== undefined && defaultOption.defaultOption !== null
      // NOTE: Child option is optional! But if it would have a set child option, there is 100% a parent option set!
    );
  }
  public static hasMultipleDefaultValues(defaultOption: DTOGetDefaultOption | null, fieldType: CustomFieldType): boolean {
    return (
      defaultOption !== undefined &&
      defaultOption !== null &&
      (fieldType === CustomFieldType.MULTICHECKBOXES || fieldType === CustomFieldType.MULTISELECT || fieldType === CustomFieldType.SOURCESENSEMULTILEVELCASCADING) &&
      defaultOption.defaultOptions !== undefined &&
      defaultOption.defaultOptions !== null &&
      defaultOption.defaultOptions.length > 0
    );
  }

  public static calculateDTOSetDefaultOption(
    curentDefaultOption: DTOGetDefaultOption,
    fieldType: CustomFieldType,
    optionToSetAsDefault: DTOCustomFieldOption,
    parentOptionIds: null | string[]
  ): FieldEditorOptionActionSetDefault | FieldEditorOptionActionUnSetDefault {
    const unsetAction = new FieldEditorOptionActionUnSetDefault();
    const setAction = new FieldEditorOptionActionSetDefault({ fieldId: 'customfield_-1', contextId: -1, doFullDelete: false, optionId: undefined, childOptionId: undefined, optionIds: undefined });
    if (curentDefaultOption && optionToSetAsDefault) {
      //
      // SINGLE DEFAULT VALUE
      //
      if (fieldType === CustomFieldType.SINGLESEELCT || fieldType === CustomFieldType.RADIOBUTTONS) {
        if (!curentDefaultOption.defaultOption || !curentDefaultOption.defaultOption.optionId || `${curentDefaultOption.defaultOption.optionId}` !== `${optionToSetAsDefault.id}`) {
          // SET DEFAULT
          setAction.defaultOption.optionId = optionToSetAsDefault.id;
          return setAction;
        }
        if (`${curentDefaultOption.defaultOption.optionId}` === `${optionToSetAsDefault.id}`) {
          // UNSET DEFAULT - CURRENTLY NOT WORKING ON SERVER DIRECTLY
          return unsetAction;
        }
      }
      //
      // CASCADING DEFAULT VALUE
      //
      if (fieldType === CustomFieldType.CASCADINGSELECT) {
        if (parentOptionIds !== null && parentOptionIds.length === 1) {
          /* MAJOR CASE 1: Child option level */
          if (
            curentDefaultOption.defaultOption &&
            curentDefaultOption.defaultChildOption &&
            curentDefaultOption.defaultOption.optionId === getLastOptionIdOrNull(parentOptionIds) &&
            `${curentDefaultOption.defaultChildOption.optionId}` === `${optionToSetAsDefault.id}`
          ) {
            // UNSET DEFAULT
            return unsetAction;
          } else {
            // SET DEFAULT
            setAction.defaultOption.optionId = getLastOptionIdOrNull(parentOptionIds);
            setAction.defaultOption.childOptionId = optionToSetAsDefault.id;
            return setAction;
          }
        } else {
          /* MAJOR CASE 2: Parent option level */
          if (curentDefaultOption.defaultOption && `${curentDefaultOption.defaultOption.optionId}` === `${optionToSetAsDefault.id}`) {
            // UNSET DEFAULT
            return unsetAction;
          } else {
            // SET DEFAULT
            setAction.defaultOption.optionId = optionToSetAsDefault.id;
            setAction.defaultOption.childOptionId = null;
            return setAction;
          }
        }
      }
      //
      // SOURCESENSE MULTI-LEVEL-CASCADING
      //
      if (fieldType === CustomFieldType.SOURCESENSEMULTILEVELCASCADING) {
        // UNSET DEFAULT
        // // LVL1: parent option is default and is clicked
        if (optionToSetAsDefault.id && `${getLastOptionIdOrNull(parentOptionIds)}` === `${optionToSetAsDefault.id}`) {
          return unsetAction;
        }
        // // LVL2+: child option is default and is clicked
        else if (optionToSetAsDefault.id && DefaultOptionHelper.isCurrentlyDefaultOption(curentDefaultOption, CustomFieldType.SOURCESENSEMULTILEVELCASCADING, optionToSetAsDefault, undefined)) {
          return unsetAction;
        }
        // SET DEFAULT
        else if (optionToSetAsDefault.id) {
          const optionIdChain = parentOptionIds && optionToSetAsDefault.id ? [...parentOptionIds, optionToSetAsDefault.id] : [optionToSetAsDefault.id];
          setAction.defaultOption.optionIds = optionIdChain;
          return setAction;
        }
      }
      //
      // MULTIPLE DEFAULT VALUE
      //
      if (fieldType === CustomFieldType.MULTICHECKBOXES || fieldType === CustomFieldType.MULTISELECT) {
        setAction.defaultOption.optionIds = curentDefaultOption.defaultOptions ? curentDefaultOption.defaultOptions.map((o) => o.optionId) : [];
        if (optionToSetAsDefault.id) {
          const indexOfOption = optionToSetAsDefault.id ? setAction.defaultOption.optionIds.indexOf(optionToSetAsDefault.id) : -1;
          if (indexOfOption === -1) {
            // SET DEFAULT
            setAction.defaultOption.optionIds.push(optionToSetAsDefault.id);
            return setAction;
          } else {
            // UNSET DEFAULT
            setAction.defaultOption.optionIds.splice(indexOfOption, 1);
            if (setAction.defaultOption.optionIds.length === 0) {
              return unsetAction;
            } else {
              return setAction;
            }
          }
        }
      }
    }
    return unsetAction; // fallback
  }

  public static isCurrentlyDefaultOption(curentDefaultOption: DTOGetDefaultOption, fieldType: CustomFieldType, option: DTOCustomFieldOption, parentOptionId?: null | string): boolean {
    if (DefaultOptionHelper.hasSingleDefaultValue(curentDefaultOption, fieldType)) {
      return (
        option !== undefined &&
        option !== null &&
        curentDefaultOption.defaultOption !== undefined &&
        curentDefaultOption.defaultOption !== null &&
        `${curentDefaultOption.defaultOption.optionId}` === `${option.id}`
      );
    }
    if (DefaultOptionHelper.hasMultipleDefaultValues(curentDefaultOption, fieldType)) {
      return (
        option !== undefined &&
        option !== null &&
        option.id !== undefined &&
        option.id !== null &&
        curentDefaultOption.defaultOptions !== undefined &&
        curentDefaultOption.defaultOptions !== null &&
        curentDefaultOption.defaultOptions.findIndex((o) => `${o.optionId}` === `${option.id}`) !== -1
      );
    }
    if (DefaultOptionHelper.hasCascadingDefaultValues(curentDefaultOption, fieldType)) {
      return (
        option !== undefined &&
        option !== null &&
        option.id !== undefined &&
        option.id !== null &&
        /* PARENT OPTION ONLY */
        ((curentDefaultOption.defaultOption !== undefined && curentDefaultOption.defaultOption !== null && `${curentDefaultOption.defaultOption.optionId}` === `${option.id}`) ||
          /* PARENT + CHILD OPTION*/
          (curentDefaultOption.defaultOption !== undefined &&
            curentDefaultOption.defaultOption !== null &&
            curentDefaultOption.defaultChildOption !== undefined &&
            curentDefaultOption.defaultChildOption !== null &&
            `${curentDefaultOption.defaultOption.optionId}` === `${parentOptionId}` &&
            `${curentDefaultOption.defaultChildOption.optionId}` === `${option.id}`))
      );
    }
    return false;
  }

  public static updateDefaultOptionStateOnDelete(
    fieldType: CustomFieldType,
    defaultOption: DTOGetDefaultOption,
    option: DTOCustomFieldOption,
    parentIds: string[] | null
  ): UpdateDefaultOptionOnDelete {
    const result = {
      hasChange: false,
      defaultOption: new DTOGetDefaultOptionImpl(),
    } as UpdateDefaultOptionOnDelete;
    const isCurrentlyDefaultOption = DefaultOptionHelper.isCurrentlyDefaultOption(defaultOption, fieldType, option, getLastOptionIdOrNull(parentIds));

    if (isCurrentlyDefaultOption) {
      result.hasChange = true;
      if (fieldType === CustomFieldType.CASCADINGSELECT || fieldType === CustomFieldType.SOURCESENSEMULTILEVELCASCADING) {
        result.defaultOption = new DTOGetDefaultOptionImpl();
      }
      if ((fieldType === CustomFieldType.MULTISELECT || fieldType === CustomFieldType.MULTICHECKBOXES) && defaultOption.defaultOptions) {
        const nextOptions = [...defaultOption.defaultOptions];
        const indexToDelete = nextOptions.findIndex((o) => o.optionId === option.id);
        if (indexToDelete !== -1) {
          nextOptions.splice(indexToDelete, 1);
        }
        result.defaultOption = new DTOGetDefaultOptionImpl();
        result.defaultOption.defaultOptions = nextOptions;
      }
      if ((fieldType === CustomFieldType.SINGLESEELCT || fieldType === CustomFieldType.RADIOBUTTONS) && defaultOption.defaultOption) {
        result.defaultOption = new DTOGetDefaultOptionImpl();
      }
    }
    return result;
  }

  public static getTranslationKeyForSetDefaultButtonTooltip(isCurrentlyDefaultParentOption: boolean, isDefaultOption: boolean, isDisabledBecauseFieldTypeCascadingAndParentOption: boolean): string {
    if (isCurrentlyDefaultParentOption) {
      return 'fieldEditor.defaultOption.isDefaultParent';
    }
    if (isDisabledBecauseFieldTypeCascadingAndParentOption) {
      return 'fieldEditor.defaultOptionIsSetInChildOptions';
    }
    if (isDefaultOption) {
      return 'fieldEditor.defaultOption.isDefault';
    } else {
      return 'fieldEditor.defaultOption.isNotDefault';
    }
  }

  // Compares the prev and next object for changes (deep inspect)
  public static hasChangeDefaultOptionByIdForUseEffect(prevDefaultOption: DTOGetDefaultOption, nextDefaultOption: DTOGetDefaultOption): boolean {
    //
    // TRIVIAL CASES
    //
    if (prevDefaultOption !== null && nextDefaultOption === null) {
      // CASE 1: the next option is null, so there is change!
      return true;
    }
    if (prevDefaultOption === null && nextDefaultOption === null) {
      // CASE 2: no change, both null
      return false;
    }
    if (__isEmptyDefaultOptionObject(prevDefaultOption) && !__isEmptyDefaultOptionObject(nextDefaultOption)) {
      // CASE 3: prev object is {} meaning "unset default" and next object is not {}, then change
      return true;
    }
    //
    // CASES FOR SINGLE SELECT + CASCADING PARENT
    //
    if (
      (__hasDefaultOptionWithDefinedId(prevDefaultOption) && !__hasDefaultOptionWithDefinedId(nextDefaultOption)) ||
      (!__hasDefaultOptionWithDefinedId(prevDefaultOption) && __hasDefaultOptionWithDefinedId(nextDefaultOption)) ||
      (__hasDefaultOptionWithDefinedId(prevDefaultOption) &&
        __hasDefaultOptionWithDefinedId(nextDefaultOption) &&
        prevDefaultOption.defaultOption!.optionId !== nextDefaultOption.defaultOption!.optionId)
    ) {
      return true;
    }
    //
    // CASES FOR CASCADING SELECT CHILD
    //
    if (
      (__hasDefaultChildOptionWithDefinedId(prevDefaultOption) && !__hasDefaultChildOptionWithDefinedId(nextDefaultOption)) ||
      (!__hasDefaultChildOptionWithDefinedId(prevDefaultOption) && __hasDefaultChildOptionWithDefinedId(nextDefaultOption)) ||
      (__hasDefaultChildOptionWithDefinedId(prevDefaultOption) &&
        __hasDefaultChildOptionWithDefinedId(nextDefaultOption) &&
        prevDefaultOption.defaultChildOption!.optionId !== nextDefaultOption.defaultChildOption!.optionId)
    ) {
      return true;
    }
    //
    // CASES FOR MULTI SELECT
    //
    if (
      (__hasDefaultMultiOptionsWithDefinedIds(prevDefaultOption) && !__hasDefaultMultiOptionsWithDefinedIds(nextDefaultOption)) ||
      (!__hasDefaultMultiOptionsWithDefinedIds(prevDefaultOption) && __hasDefaultMultiOptionsWithDefinedIds(nextDefaultOption)) ||
      __hasDifferentDefaultMultiOptionsWithDefinedIds(prevDefaultOption, nextDefaultOption)
    ) {
      return true;
    }
    // ELSE
    return false;
  }

  public static enrichOptionValuesOnExistingDefaultOptionsObjectWithValuePlaceholders(
    curentDefaultOption: DTOGetDefaultOption,
    fieldType: CustomFieldType,
    allOptions: DTOCustomFieldOption[]
  ): DTOGetDefaultOption {
    if (allOptions && curentDefaultOption && curentDefaultOption.defaultOption && DefaultOptionHelper.hasSingleDefaultValue(curentDefaultOption, fieldType)) {
      if (curentDefaultOption.defaultOption.optionId) {
        const optionFound = allOptions.find((o) => `${o.id}` === `${curentDefaultOption.defaultOption?.optionId}`);
        return {
          defaultOption: {
            optionId: curentDefaultOption.defaultOption.optionId,
            optionValue: optionFound ? optionFound.value : '???',
          },
        };
      }
    }
    if (allOptions && curentDefaultOption && curentDefaultOption.defaultOptions && DefaultOptionHelper.hasMultipleDefaultValues(curentDefaultOption, fieldType)) {
      const result: DTOGetDefaultOptionItem[] = [];
      for (const currOpt of curentDefaultOption.defaultOptions) {
        if (currOpt.optionId) {
          const found = allOptions.find((o) => `${o.id}` === `${currOpt.optionId}`);
          result.push({
            optionId: currOpt.optionId,
            optionValue: found ? found.value : '???',
          } as DTOGetDefaultOptionItem);
        }
      }
      return {
        defaultOptions: result,
      };
    }
    if (allOptions && curentDefaultOption && curentDefaultOption.defaultOption && DefaultOptionHelper.hasCascadingDefaultValues(curentDefaultOption, fieldType)) {
      const result = {} as DTOGetDefaultOption;
      let hasSomeDefault = false;
      if (curentDefaultOption.defaultOption.optionId) {
        const parentOptionFound = allOptions.find((o) => `${o.id}` === `${curentDefaultOption.defaultOption?.optionId}`);
        result.defaultOption = {
          optionId: curentDefaultOption.defaultOption.optionId,
          optionValue: parentOptionFound ? parentOptionFound.value : '???',
        };
        hasSomeDefault = true;
      }
      if (curentDefaultOption.defaultChildOption && curentDefaultOption.defaultChildOption.optionId) {
        result.defaultChildOption = {
          optionId: curentDefaultOption.defaultChildOption.optionId,
          optionValue: '???', // NOTE: We do not have child options to query here. Is done somewhere else, see FieldEditorStatefull useEffect!
        };
        hasSomeDefault = true;
      }
      if (hasSomeDefault === true) {
        return result;
      }
    }
    // DO NOTHING ON ERROR AND RETURN PASSED OBJECT INSTEAD!
    return curentDefaultOption;
  }

  public static generateGetDefaultOptionObjectFromSetDefaultOptionObject(setObj: DTOSetDefaultOption): DTOGetDefaultOption {
    return {
      // Note: optionValues are set somewhere else automatically
      defaultOption: setObj.optionId ? { optionId: setObj.optionId, optionValue: '...' } : undefined,
      defaultChildOption: setObj.childOptionId ? { optionId: setObj.childOptionId, optionValue: '...' } : undefined,
      defaultOptions: setObj.optionIds
        ? setObj.optionIds.map((id) => ({
            optionId: id,
            optionValue: '...',
          }))
        : undefined,
    } as DTOGetDefaultOption;
  }
}

export interface UpdateDefaultOptionOnDelete {
  hasChange: boolean;
  defaultOption: DTOGetDefaultOption;
}
